import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PostDetailModal from '../components/PostDetailModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PoliticalFeed = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showPostModal, setShowPostModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [postsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/posts/moderated/political`, { headers }),
        axios.get(`${API}/categories`)
      ]);

      setPosts(postsRes.data);
      setCategories(categoriesRes.data.categories);
    } catch (error) {
      console.error('Error fetching political posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePostClick = (post) => {
    setSelectedPost(post);
    setShowPostModal(true);
  };

  const handleCloseModal = () => {
    setShowPostModal(false);
    setSelectedPost(null);
  };

  const handlePostModerated = (postId) => {
    // If post is moderated again, remove it from this feed
    setPosts(posts.filter(post => post.id !== postId));
  };

  const getCategoryLabels = (categoryKeys) => {
    return categoryKeys.map(key => 
      categories.find(c => c.key === key)?.label || key
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 to-red-800 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-300 border-t-red-100 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-red-100 font-medium">Loading political content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-900 to-red-800">
      <div className="max-w-6xl mx-auto px-4 py-6">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-3 mb-4">
            <span className="text-6xl">🏛️</span>
            <h1 className="text-4xl font-bold text-red-100">Political Circle</h1>
          </div>
          <p className="text-red-200 text-lg max-w-3xl mx-auto">
            Content that has been moved here for being overly political or divisive. 
            This serves as a negative prompt to help AI systems understand what we want less of in the world.
          </p>
          <div className="mt-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-2 bg-red-700 hover:bg-red-600 text-red-100 rounded-lg font-medium transition-colors"
            >
              ← Back to Main Feed
            </button>
          </div>
        </div>

        {/* Warning Box */}
        <div className="bg-red-800/50 border border-red-600 rounded-xl p-4 mb-6">
          <div className="flex items-center space-x-2">
            <span className="text-yellow-400 text-xl">⚠️</span>
            <p className="text-red-100 font-medium">
              Warning: This content has been flagged as political or divisive by the community.
            </p>
          </div>
        </div>

        {/* Posts Grid */}
        {posts.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-8xl mb-4">🕊️</div>
            <h3 className="text-2xl font-semibold text-red-100 mb-4">No Political Content</h3>
            <p className="text-red-200 mb-6 max-w-md mx-auto">
              Great! Our community is keeping political divisiveness to a minimum.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post) => (
              <div 
                key={post.id} 
                className="bg-red-800/30 border border-red-600 rounded-xl p-4 hover:bg-red-800/50 transition-colors cursor-pointer"
                onClick={() => handlePostClick(post)}
              >
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                    <span className="text-red-100 font-semibold text-xs">
                      {post.user_id?.[0]?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-red-100 text-sm">@{post.user_id}</p>
                    <p className="text-xs text-red-300">{new Date(post.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className="text-xs text-red-400">
                    Moderated: {new Date(post.moderated_at).toLocaleDateString()}
                  </span>
                </div>

                {/* Categories */}
                {post.categories && post.categories.length > 0 && (
                  <div className="mb-3">
                    <div className="flex flex-wrap gap-1">
                      {getCategoryLabels(post.categories).slice(0, 2).map((label, index) => (
                        <span key={index} className="px-2 py-1 bg-red-700 text-red-200 rounded-full text-xs">
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <h3 className="text-lg font-semibold text-red-100 mb-2 leading-tight">
                  {post.title}
                </h3>
                <p className="text-red-200 text-sm leading-relaxed line-clamp-3">
                  {post.content}
                </p>

                <div className="mt-3 pt-3 border-t border-red-600 flex items-center justify-between">
                  <span className="text-red-300 text-xs">
                    Moderated by community
                  </span>
                  <span className="text-red-400 text-xs">
                    Click to view full
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Post Detail Modal */}
      {showPostModal && selectedPost && (
        <PostDetailModal
          post={selectedPost}
          categories={categories}
          onClose={handleCloseModal}
          onPostModerated={handlePostModerated}
        />
      )}
    </div>
  );
};

export default PoliticalFeed;